import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { editContent, InnerContent } from '../../../modules/editor';
import { RootState } from '../../../modules';
import Upload from '../../../components/Upload/Upload';
import ImageToolbar from '../Toolbar/ImageToolbar';
import { parseStyle } from '../../../util/parseStyle';
import { dummyImagePath, 	dummyImageSmallPath } from '../../../config/components';
import { valueToNumber } from '../../../util/string';
import { useHistoryMode } from '../../History/HistoryContext';
import classNames from 'classnames';
import useIsImageUploading from '../../../hooks/useIsImageUploading';
import Spinner from '../../../components/Spinner';


type Props = {
	innerContent: InnerContent;
	style: any;
	contentId: number;
	contentIndex: number;
	subIndex?: number;
	columnCount: number;
	editing: boolean;
	markToolbar: (contentId: number, currentIndex: number, itemType: string) => void;
	markedToolbar: any
};

function ImageEdit({ innerContent, style, contentId, contentIndex, subIndex, columnCount, editing, markToolbar, markedToolbar }: Props) {
	const { isInHistory } = useHistoryMode();
	const dispatch = useDispatch();

	const [showToolbar, setShowToolbar] = useState(false);	
	const [imageInfo, setImageInfo] = useState({} as any);
	const [maxWidth, setMaxWidth] = useState(600);

	const editor = useSelector((state: RootState) => state.editor);
	const selectedContentId = useSelector((state: RootState) => state.editor.selectedContentId);
	const screenMode = useSelector((state: RootState) => state.editor.ui.screenMode);

	const isDummyImage = useMemo(() => innerContent?.imagePath?.includes('dummy_image'), [innerContent?.imagePath]);
	const imgRef = useRef<HTMLImageElement>(null);
	
	const contentTarget = subIndex !== undefined ? `${contentId}/content/${contentIndex}/${subIndex}` : `${contentId}/content/${contentIndex}`;	
	const styleTarget = subIndex !== undefined ? `${contentId}/style/${contentIndex}/${subIndex}` : `${contentId}/style/${contentIndex}`;

	useEffect(() => {
		if (isInHistory) return;
		if (isDummyImage) return;
		setTimeout(() => {
			if (imgRef?.current && screenMode === 'desktop') {
				dispatch(editContent({ target: `${styleTarget}/image/originalWidth`, data: `${imgRef.current?.width}`}));
			}
		}, 100);
	}, []);
	
	useEffect(() => {
		if (isDummyImage) return;
		setTimeout(() => {
			if (imgRef.current) {
				setImageInfo(imgRef.current);
			}
		}, 100);
	}, [editor]);

	useEffect(() => {
		if (selectedContentId !== contentId) {
			unselectContent();
		}
	}, [selectedContentId]);

	useEffect(() => {
		if (markedToolbar?.contentId !== contentId || markedToolbar?.currentIndex !== contentIndex || markedToolbar.itemType !== 'image') {
			setShowToolbar(false);
		}
	}, [markedToolbar]);

	useEffect(() => {
		let boxPadding = valueToNumber(editor.contents?.filter((item:any) => {return item.id === contentId})[0]?.content?.style[0]?.box?.paddingLeft);

		setMaxWidth((600 - ((boxPadding-15) * 2)) / columnCount);
	}, [editor])

	const startEditing = () => {
		if (markToolbar) {
			markToolbar(contentId, contentIndex, 'image');
		}
		setShowToolbar(true);
	}

	const startEditingWithoutToolbar = () => {		
		if (markToolbar) {
			markToolbar(contentId, contentIndex, 'image');
		}
	}

	const unselectContent = () => {
		setShowToolbar(false);
	}

	const getDummyImagePath = ({ columnCount, screenMode }) => {	
		const isSmallSize = columnCount === 2 || screenMode === 'mobile';
		return isSmallSize ? dummyImageSmallPath : dummyImagePath;
	}

	const imagePath = useMemo(() => {
		if (isDummyImage) {
			return getDummyImagePath({ columnCount, screenMode });
		}
		return innerContent?.imagePath;
	}, [columnCount, screenMode, innerContent?.imagePath, isDummyImage]);

	const imagePathTarget = `${contentTarget}/imagePath`;
	const { imageRef, isImageUploading } = useIsImageUploading(imagePathTarget);

	return (
		<div className={showToolbar ? 'image-edit active' : 'image-edit'}>
			<div 
				className={classNames('image-edit-content', 'img-wrapper', isDummyImage && 'dummy')}
				style={{
					textAlign: style?.textAlign
				}}	
			>
				<img 
					src={imagePath} 
					alt={innerContent.imageAlt} 
					style={parseStyle(style)}
					ref={(node) => {
						imgRef.current = node;
						imageRef.current = node;
					}}
				/>
				{isImageUploading && <div className={classNames('img-spinner', isImageUploading && 'active')}>
					<Spinner size={18} />
				</div>}
			</div>
			{showToolbar ? (
				<>
					<ImageToolbar
						innerContent={innerContent}
						style={style}
						contentId={contentId}
						contentIndex={contentIndex}
						imageInfo={imageInfo}
						subIndex={subIndex}
						maxWidth={maxWidth}
					/>
					<Upload
						target={imagePathTarget}
						columnCount={columnCount}
						isDummy={isDummyImage}
						imageInfo={imageInfo}
						maxWidth={maxWidth}
					/>
				</>
			) : (
				isDummyImage ? (
					<div onClick={startEditingWithoutToolbar}>
						<Upload
							target={imagePathTarget}
							columnCount={columnCount}
							maxWidth={maxWidth}
						/>
					</div>					
				) : (
					<div className="common-upload cover" onClick={startEditing} />
				)
			)
		}
		</div>
	);
}

export default ImageEdit;